import React from 'react';
import { Route, IndexRedirect, Redirect } from 'react-router';
import ErrorService from '~/utils/ErrorService';
import _ from 'lodash';

import config from '~/config';

import App from './components/App';
import NotFoundPage from './components/NotFoundPage';
import LoginPage from './components/LoginPage';

import AuthService from '~/utils/AuthService';
import ApiService from '~/utils/ApiService';
import Helpers from '~/utils/Helpers';

import * as meActions from '~/actions/me';
import * as navActions from '~/sidebarNav/actions';
import * as teamActions from '~/actions/team';
import * as artifactActions from '~/artifact/actions';
import * as libraryActions from '~/library/actions';
import * as searchActions from '~/search2/actions';

import ArtifactPage from '~/artifact/containers/ArtifactPage';
import LibraryPage from '~/library/containers/LibraryPage';
import SearchResults from '~/search2/containers/SearchResults';

import { SOURCECLEAR_COMPONENTS } from '~/constants/ModelConstants';
import * as vcPageActions from '~/actions/vcPageActions';

export const getRoutes = ({ getState, dispatch }) => {
  const informPageLoaded = () => {
    if (window.opener && Helpers.isPlatformFrontendUrl(document.referrer)) {
      window.opener.postMessage('loaded', document.referrer);
    }
  };

  const listenForAuthToken = () => {
    return new Promise(resolve => {
      window.addEventListener('message', function(evt) {
        if (Helpers.isPlatformFrontendUrl(evt.origin) && evt.data && evt.data.authToken) {
          AuthService.resetAuthToken(evt.data.authToken);
          resolve();
        }
      });

      informPageLoaded();
    });
  };

  const loadInitialData = () => {
    const isVeracodeDomain = _.includes(config.VERACODE_DOMAINS, window.location.hostname);

    return Promise.all([
      ApiService.get('/me', { shouldUseVeracodeApi: isVeracodeDomain }),
      ApiService.get('/me/teams', { shouldUseVeracodeApi: isVeracodeDomain }),
    ]);
  };

  const onAppLoad = (nextState, replace, callback) => {
    // Wait 5 seconds for the message from platform-frontend about auth token before time out
    const timeoutBeforeMessageReceived = 5000;

    if (Helpers.isPlatformFrontendUrl(document.referrer)) {
      // Either we get the auth token from platform-frontend first or we will fail and log the error
      const timeOutPromise = Helpers.getTimeOutPromise(timeoutBeforeMessageReceived);

      Promise.race([listenForAuthToken(), timeOutPromise]).then(
        () => {
          // If we reach here, it means listenForAuthToken has success and token is set. Document.referrer will stay for
          // the same browser window if the user refresh the site. Thus we are going to experiment with window.location.reload()
          // which will clear the document.referrer. So user won't be stuck for 5 seconds waiting for messages from
          // platform-frontend that will not come.
          window.location.reload();

          // Otherwise if the above experiment does not wokr, we will loadApp as usual
          // loadApp(callback);
        },
        () => {
          ErrorService.capture(
            'Clicked from platform-frontend but no message recieved after ' +
              timeoutBeforeMessageReceived +
              ' ms'
          );
          loadApp(callback);
        }
      );
    } else {
      // Other possibilities such as: refreshing the page,
      loadApp(callback);
    }

    updateActiveState(null, nextState);
  };

  const loadApp = callback => {
    // If the user has session token in browser, we want to load all the user's info
    // before loading the app. This will ensure that other part of the app which does
    // paywall-check function properly.
    const isVeracodeDomain = _.includes(config.VERACODE_DOMAINS, window.location.hostname);

    // shouldShowVeracodePage is always true now (already set as the default so this could probably be removed altogether)
    dispatch(vcPageActions.updateVCPageVisibility(true));

    if (isVeracodeDomain || AuthService.getAuthToken()) {
      loadInitialData()
        .then(
          res => {
            const [me, teams] = res;
            dispatch(meActions.updateMe(me));
            dispatch(teamActions.updateTeams(teams));

            callback();
          },
          err => {
            // When user has a token but has expired and /me will return 401
            callback();
          }
        )
        .catch(err => {
          // In case of any other unknown error, dont block the user from loading the app
          callback();
        });
    } else {
      // No auth-token, proceed immediately as non-authenticated user
      callback();
    }
  };

  const updateActiveState = (prevState, nextState) => {
    const store = getState();
    const subMenuToExpand = nextState.routes.find(route => route.state) || {};
    const activeState = subMenuToExpand.state || {};
    dispatch(navActions.updateActiveStateAndParams(activeState, nextState.params));
  };

  const updateActiveTab = nextState => {
    updateActiveState(null, nextState);
    dispatch(artifactActions.updateArtifactActiveTab(nextState.params.tab));
    dispatch(searchActions.updateIsSearchMode(false));
  };

  const updateLibraryActiveTab = nextState => {
    updateActiveState(null, nextState);
    dispatch(searchActions.updateIsSearchMode(false));
    if (nextState.params.tab) {
      dispatch(libraryActions.updateTab(nextState.params.tab));

      if (!nextState.params.version) return;

      if (nextState.params.tab === 'versions') {
        dispatch(libraryActions.updateVersion(nextState.params.version));
      } else if (nextState.params.tab === 'commit_hash') {
        dispatch(libraryActions.updateVersion('HEAD'));
        const nextCommitHash = nextState.params.version;

        if (nextCommitHash) {
          const commitHash = Helpers.isCommitHash(nextCommitHash) ? nextCommitHash : '-';
          dispatch(libraryActions.updateCommitHash(commitHash));
        }
      }
    }
  };

  return (
    <Route path="/" component={App} onEnter={onAppLoad} onChange={updateActiveState}>
      <IndexRedirect to={SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug} />
      <Route path={SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}>
        <IndexRedirect to="search" />
        <Redirect from="explore" to="search" />
        <Route path="search" state={{ navMenu: 'search' }} component={SearchResults} />

        <Route
          path="libraries/:title/:language/:packageManager/lid-:componentId"
          state={{ navMenu: 'library-view' }}
          component={LibraryPage}
          onEnter={updateLibraryActiveTab}
        />
        <Route
          path="libraries/:title/:language/:packageManager/lid-:componentId/:tab"
          state={{ navMenu: 'library-view' }}
          component={LibraryPage}
          onEnter={updateLibraryActiveTab}
        />
        <Route
          path="libraries/:title/:language/:packageManager/lid-:componentId/:tab/:version"
          state={{ navMenu: 'library-view' }}
          component={LibraryPage}
          onEnter={updateLibraryActiveTab}
        />
        <Route
          path="libraries/:componentId"
          state={{ navMenu: 'library-view' }}
          component={LibraryPage}
          onEnter={updateLibraryActiveTab}
        />

        {/* A set of redirects to bring user from .../<version>/xxx/xyz/123 to /<version>/xxx URL omitting any trailing URI segment after the version */}
        <Redirect
          from="libraries/:title/:language/:packageManager/lid-:componentId/:tab/:version/*"
          to="libraries/:title/:language/:packageManager/lid-:componentId/:tab/:version"
        />
        <Redirect
          from="components/:title/:language/:packageManager/lid-:componentId/:tab/:version/*"
          to="libraries/:title/:language/:packageManager/lid-:componentId/:tab/:version"
        />

        {/* A set of redirects to bring user from /components/xxx to /libraries/xxx URL */}
        <Redirect
          from="components/:title/:language/:packageManager/lid-:componentId"
          to="libraries/:title/:language/:packageManager/lid-:componentId"
        />
        <Redirect
          from="components/:title/:language/:packageManager/lid-:componentId/:tab"
          to="libraries/:title/:language/:packageManager/lid-:componentId/:tab"
        />
        <Redirect
          from="components/:title/:language/:packageManager/lid-:componentId/:tab/:version"
          to="libraries/:title/:language/:packageManager/lid-:componentId/:tab/:version"
        />
        <Redirect from="components/:componentId" to="libraries/:componentId" />

        <Route path="security/:title/:language">
          {/* Redirect all s-xxx to sid-xxx route */}
          <Redirect from="s-:vulnId" to="sid-:vulnId" />
          <Route
            path="sid-:vulnId"
            state={{ navMenu: 'artifact-view' }}
            component={ArtifactPage}
            onEnter={updateActiveTab}
          />
          <Redirect from="s-:vulnId/:tab" to="sid-:vulnId/:tab" />
          <Route
            path="sid-:vulnId/:tab"
            state={{ navMenu: 'artifact-view' }}
            component={ArtifactPage}
            onEnter={updateActiveTab}
          />
        </Route>
        <Route
          path="vulnerabilities/:vulnId"
          state={{ navMenu: 'artifact-view' }}
          component={ArtifactPage}
          onEnter={updateActiveTab}
        />
      </Route>
      <Redirect from="login" to="registry/search" />
      <Route path="research" component={LoginPage} />
      <Route path="*" state={{ noSidebar: true }} component={NotFoundPage} />
    </Route>
  );
};
